package com.yendo.mx.repository

import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import kotlinx.serialization.json.*

/**
 * Repositorio de cálculo y obtención de rutas y distancias trazadas por calles usando OSRM.
 */
object RutaRepository {
    private val client = HttpClient()
    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Obtiene los puntos de coordenadas (lat, lng) para trazar la ruta real por calles.
     * @param puntos Lista de puntos clave (lat, lng) en orden: [Origen, ..., Destino]
     * @return Lista de puntos (lat, lng) que forman la ruta trazada por calles.
     */
    suspend fun obtenerPuntosRuta(puntos: List<Pair<Double, Double>>): List<Pair<Double, Double>> {
        if (puntos.size < 2) return emptyList()

        return try {
            // OSRM utiliza formato /lng,lat;lng,lat
            val waypointsStr = puntos.joinToString(";") { (lat, lng) ->
                "$lng,$lat"
            }
            val url = "https://router.project-osrm.org/route/v1/driving/$waypointsStr?overview=full&geometries=geojson"

            println("YENDO_RUTA_LOG: Solicitando ruta a OSRM: $url")
            val response = client.get(url) {
                header("User-Agent", "YendoApp/1.0 (Android; KMP)")
            }

            if (response.status.value in 200..299) {
                val bodyText = response.bodyAsText()
                val root = json.parseToJsonElement(bodyText).jsonObject
                val routes = root["routes"]?.jsonArray
                if (!routes.isNullOrEmpty()) {
                    val geometry = routes[0].jsonObject["geometry"]?.jsonObject
                    val coordinates = geometry?.get("coordinates")?.jsonArray
                    if (coordinates != null) {
                        val resultado = coordinates.mapNotNull { pointElement ->
                            val arr = pointElement.jsonArray
                            if (arr.size >= 2) {
                                val lng = arr[0].jsonPrimitive.double
                                val lat = arr[1].jsonPrimitive.double
                                Pair(lat, lng)
                            } else null
                        }
                        println("YENDO_RUTA_LOG: Ruta obtenida con ${resultado.size} puntos.")
                        return resultado
                    }
                }
            }
            println("YENDO_RUTA_LOG: OSRM respondió sin rutas válidas (${response.status.value}). Usando ruta directa.")
            emptyList()
        } catch (e: Exception) {
            println("YENDO_RUTA_LOG: Error al obtener ruta OSRM: ${e.message}")
            emptyList()
        }
    }

    /**
     * Obtiene la distancia exacta por calles en Kilómetros entre dos puntos usando OSRM.
     * Si la consulta falla o no hay conexión, hace fallback a la distancia Haversine.
     */
    suspend fun obtenerDistanciaKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        if (lat1 == 0.0 || lon1 == 0.0 || lat2 == 0.0 || lon2 == 0.0) return 0.0

        return try {
            val url = "https://router.project-osrm.org/route/v1/driving/$lon1,$lat1;$lon2,$lat2?overview=false"
            println("YENDO_RUTA_LOG: Solicitando distancia OSRM: $url")
            val response = client.get(url) {
                header("User-Agent", "YendoApp/1.0 (Android; KMP)")
            }

            if (response.status.value in 200..299) {
                val bodyText = response.bodyAsText()
                val root = json.parseToJsonElement(bodyText).jsonObject
                val routes = root["routes"]?.jsonArray
                if (!routes.isNullOrEmpty()) {
                    val distanceMeters = routes[0].jsonObject["distance"]?.jsonPrimitive?.double
                    if (distanceMeters != null) {
                        val distKm = distanceMeters / 1000.0
                        println("YENDO_RUTA_LOG: Distancia OSRM por calles: $distKm km")
                        return distKm
                    }
                }
            }
            println("YENDO_RUTA_LOG: OSRM falló (${response.status.value}). Usando Haversine fallback.")
            com.yendo.mx.utils.GeoUtils.calcularDistanciaKm(lat1, lon1, lat2, lon2)
        } catch (e: Exception) {
            println("YENDO_RUTA_LOG: Error consultando distancia OSRM (${e.message}). Usando Haversine.")
            com.yendo.mx.utils.GeoUtils.calcularDistanciaKm(lat1, lon1, lat2, lon2)
        }
    }
}
